import { useState, lazy, Suspense } from "react";
import {
  SkyAvatar,
  SkyButton,
  SkyText,
  SkyLink,
  SkySkeleton,
  SkySkeletonItem,
  SkyLoader,
} from "@skypointcloud/skypoint-ui";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { ExternalLink, LogOut } from "lucide-react";
import { useCurrentUser } from "../hooks";
import { TenantInstanceSwitcher } from "./TenantInstanceSwitcher";
import { PortalViewDropdown } from "./PortalViewDropdown";
import { ProductSwitcher } from "./ProductSwitcher";
import { dateFormatter } from "@/common/utilities/dateFormatter";
import { shownEmail } from "../../../../../shared/identity/signInAlias";

// Lazy-load dialogs - only loaded when user opens them
const FeedbackDialog = lazy(() =>
  import("./FeedbackDialog").then((m) => ({ default: m.FeedbackDialog })),
);

interface ProfileMenuProps {
  /** When omitted, the "View profile" link is hidden — e.g. the customer portal
   *  shell has no account/profile route, so linking there dead-ended (#946). */
  onViewAccount?: () => void;
  onSignOut: () => void;
}

// Returns true if the event target lives inside a Radix popper portal
// (e.g. SkyDropdown listbox), which is rendered as a sibling of our popover
// content and would otherwise be treated as "outside".
function isInNestedPopper(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el?.closest?.("[data-radix-popper-content-wrapper], [role='listbox'], [role='option']");
}

export function ProfileMenu({ onViewAccount, onSignOut }: ProfileMenuProps) {
  // Get user data from hook
  const { currentUser, initials, isLoaded: isUserLoaded } = useCurrentUser();

  // Dialog states
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // Context change loading state (for tenant/instance switch)
  const [isChangingContext, setIsChangingContext] = useState(false);

  // Menu state - Fluent UI Popover handles click-outside automatically including nested portals
  const [menuOpen, setMenuOpen] = useState(false);

  const name = currentUser?.fullname || "";
  // A phone-only person signs in under an alias: no address to show.
  const email = shownEmail(currentUser?.email as string | undefined);
  const lastLogin = currentUser?.lastLogin ? dateFormatter(currentUser.lastLogin as string) : null;

  return (
    <>
      <div className="relative">
        {/* Trigger */}
        {!isUserLoaded ? (
          <SkySkeleton
            aria-label="Loading user"
            className="flex items-center gap-2"
          >
            <SkySkeletonItem
              shape="circle"
              className="w-8! h-8!"
            />
            <SkySkeletonItem className="w-24! h-4! hidden! md:block!" />
          </SkySkeleton>
        ) : (
          <PopoverPrimitive.Root
            open={menuOpen}
            onOpenChange={setMenuOpen}
          >
            <PopoverPrimitive.Trigger asChild>
              <div
                className="flex items-center gap-2 cursor-pointer"
                role="button"
                tabIndex={0}
              >
                <SkyAvatar
                  name={initials}
                  size={32}
                  className="hover:ring-2 hover:ring-white/30 transition-all"
                  color="brand"
                />
                {name && (
                  <SkyText
                    size={400}
                    weight="medium"
                    className="text-white hidden! md:block!"
                  >
                    {name}
                  </SkyText>
                )}
              </div>
            </PopoverPrimitive.Trigger>
            <PopoverPrimitive.Portal>
              <PopoverPrimitive.Content
                align="end"
                sideOffset={8}
                // z-[1050] aligns with the codebase z-index scale (--z-modal in
                // index.css). Higher values like z-[99999] caused nested
                // SkyDropdown listboxes to render BEHIND this popover — Radix
                // popper portals are bumped to z-1100 by the global rule in
                // index.css:76, so the parent must sit below 1100.
                className="p-0 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 min-w-75 bg-white dark:bg-gray-900 z-[1050]"
                // Nested SkyDropdown/Select listboxes portal into a separate
                // [data-radix-popper-content-wrapper]. Without these guards on
                // BOTH pointer-down AND focus events, clicking the Tenant/
                // Instance/Portal-View dropdown closes this popover — its
                // listbox unmounts mid-click and the dropdowns appear
                // unclickable. Radix popover treats the three events
                // independently; preventDefault on one doesn't cover the others.
                onPointerDownOutside={(e) => {
                  if (isInNestedPopper(e.target)) e.preventDefault();
                }}
                onFocusOutside={(e) => {
                  if (isInNestedPopper(e.target)) e.preventDefault();
                }}
                onInteractOutside={(e) => {
                  if (isInNestedPopper(e.target)) e.preventDefault();
                }}
              >
                {/* Profile Header */}
                <div className="flex items-start gap-3 p-4 border-b border-gray-200 dark:border-gray-700">
                  <SkyAvatar
                    name={initials}
                    size={32}
                    color="brand"
                  />
                  <div className="flex flex-col min-w-0 flex-1">
                    <SkyText
                      weight="semibold"
                      className="text-gray-900 dark:text-gray-100 truncate"
                      size={300}
                    >
                      {name}
                    </SkyText>
                    {email && (
                      <SkyText
                        size={200}
                        className="text-gray-500 dark:text-gray-400 truncate"
                      >
                        {email}
                      </SkyText>
                    )}
                    {onViewAccount && (
                      <SkyLink
                        onClick={() => {
                          onViewAccount();
                          setMenuOpen(false);
                        }}
                        className="text-xs mt-1"
                      >
                        View profile
                      </SkyLink>
                    )}
                    {lastLogin && (
                      <SkyText
                        size={200}
                        className="text-gray-400 mt-0.5"
                      >
                        Last login: {lastLogin}
                      </SkyText>
                    )}
                  </div>
                </div>

                {/* Tenant/Instance Switcher */}
                <TenantInstanceSwitcher
                  onContextChange={() => setMenuOpen(false)}
                  onLoadingChange={setIsChangingContext}
                />

                {/* Product Switcher (mobile) */}
                <div className="md:hidden!">
                  <ProductSwitcher
                    variant="menu"
                    onNavigate={() => setMenuOpen(false)}
                  />
                </div>

                {/* Portal View Switcher */}
                <PortalViewDropdown onViewChange={() => setMenuOpen(false)} />

                {/* Menu Items */}
                <div className="py-1 flex flex-col">
                  <SkyButton
                    appearance="transparent"
                    size="small"
                    className="justify-between! px-4!"
                    onClick={() => {
                      window.open("https://docs.skypointcloud.com/docs/intro", "_blank");
                      setMenuOpen(false);
                    }}
                  >
                    Documentation
                    <ExternalLink className="w-4 h-4 text-gray-400" />
                  </SkyButton>

                  <SkyButton
                    appearance="transparent"
                    size="small"
                    className="justify-between! px-4!"
                    onClick={() => {
                      window.open("https://privacy.skypoint.ai/", "_blank");
                      setMenuOpen(false);
                    }}
                  >
                    Privacy Portal
                    <ExternalLink className="w-4 h-4 text-gray-400" />
                  </SkyButton>

                  <SkyButton
                    appearance="transparent"
                    size="small"
                    className="justify-between! px-4!"
                    onClick={() => {
                      window.open(
                        "https://skypoint.ai/master-services-agreement-8-7-2025/",
                        "_blank",
                      );
                      setMenuOpen(false);
                    }}
                  >
                    Terms of Use
                    <ExternalLink className="w-4 h-4 text-gray-400" />
                  </SkyButton>

                  <SkyButton
                    appearance="transparent"
                    size="small"
                    className="justify-start! px-4!"
                    onClick={() => {
                      setShowFeedbackModal(true);
                      setMenuOpen(false);
                    }}
                  >
                    Feedback
                  </SkyButton>
                </div>

                {/* Logout & Version */}
                <div className="p-4 pt-2 flex flex-col gap-2 border-t border-gray-200 dark:border-gray-700">
                  <SkyButton
                    appearance="primary"
                    size="small"
                    icon={LogOut}
                    onClick={() => {
                      onSignOut();
                      setMenuOpen(false);
                    }}
                    className="w-full"
                  >
                    Logout
                  </SkyButton>
                  <SkyText
                    size={200}
                    className="text-gray-400 text-center"
                  >
                    Version {__APP_VERSION__}
                  </SkyText>
                </div>
              </PopoverPrimitive.Content>
            </PopoverPrimitive.Portal>
          </PopoverPrimitive.Root>
        )}
      </div>

      {/* Feedback Dialog - lazy loaded */}
      {showFeedbackModal && (
        <Suspense fallback={null}>
          <FeedbackDialog
            open={showFeedbackModal}
            onOpenChange={setShowFeedbackModal}
          />
        </Suspense>
      )}

      {/* Blocking overlay during tenant/instance context change */}
      {isChangingContext && (
        <div className="fixed inset-0 z-99999 bg-white dark:bg-gray-900 flex items-center justify-center">
          <SkyLoader
            size="large"
            label="Loading..."
          />
        </div>
      )}
    </>
  );
}
